(() => {
  'use strict';
  const VERSION = '15.7.1';
  const qs = (s, r=document) => r.querySelector(s);
  const qsa = (s, r=document) => Array.from(r.querySelectorAll(s));
  const clean = (v) => String(v ?? '').trim();
  const canonicalFulfillmentGroup = (value) => {
    const group = clean(value).toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    if (['local_dtf','apparel_print','blank_apparel','gang_sheet','ready_to_wear','personalized_apparel','custom_apparel','personalized','custom','custom_order'].includes(group)) return 'local_dtf';
    if (['personalized_items','personalized_gifts','engraved_gifts'].includes(group)) return 'personalized_items';
    if (['calca','calca_dropship','dtf_supplies'].includes(group)) return 'calca_dropship';
    if (['wedding','wedding_stationery'].includes(group)) return 'wedding';
    if (['business','business_services'].includes(group)) return 'business';
    return group;
  };
  const paidRules = () => window.MEPlacementRules?.placements || {};
  const splitPlacements = (value) => clean(value).split(',').map(v => v.trim()).filter(Boolean);

  function renderedMeta() {
    const node = qs('[data-me-cart-security-meta]');
    if (!node) return [];
    try { return JSON.parse(node.textContent || '[]'); } catch (_) { return []; }
  }

  async function getCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to verify cart integrity.');
    return response.json();
  }


  function brandingCreditEligible(props = {}) {
    const mode = clean(props['_ME Neck Label Mode']).toLowerCase();
    const label = clean(props['Inside Neck Label']).toLowerCase();
    return mode === 'me-branding' || label.includes('moments etched branding');
  }

  function brandingDiscountAmount(line) {
    const discounts = Array.isArray(line?.discounts) ? line.discounts : [];
    return discounts.reduce((sum, discount) => {
      const title = clean(discount?.title).toLowerCase();
      if (!(title.includes('moment') && title.includes('branding'))) return sum;
      return sum + Number(discount?.amount || 0);
    }, 0);
  }


  const cents = (value) => Math.round(Number(value || 0));

  function expectedPersonalizedSavings(cart) {
    const garments = (cart.items || []).filter((line) => {
      const props = line.properties || {};
      return clean(props['_ME Design Group']) && clean(props['_ME Line Type']) === 'garment';
    });
    const totalQuantity = garments.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    const groupPercent = totalQuantity >= 21 ? 0 : totalQuantity >= 11 ? 10 : totalQuantity >= 6 ? 5 : 0;

    // v15.4.7.28: branding is a product discount that should already be in
    // cart.js; group savings is an order discount and may not be represented in
    // cart totals until checkout. Project the group tier from the current
    // post-product-discount garment merchandise subtotal instead of requiring
    // cart.js to contain the checkout-stage order discount.
    let brandingExpected = 0;
    let brandingActual = 0;
    let brandingIneligible = 0;
    let groupBase = 0;
    let originalTotal = 0;
    for (const line of garments) {
      const quantity = Math.max(0, Number(line.quantity || 0));
      const eligible = brandingCreditEligible(line.properties || {});
      const actualBranding = cents(brandingDiscountAmount(line));
      originalTotal += cents(line.original_line_price);
      groupBase += cents(line.final_line_price);
      if (eligible) {
        brandingExpected += 200 * quantity;
        brandingActual += actualBranding;
      } else {
        brandingIneligible += actualBranding;
      }
    }

    const groupExpected = groupPercent > 0
      ? Math.floor((Math.max(0, groupBase) * groupPercent) / 100)
      : 0;
    return {
      totalQuantity, groupPercent, groupExpected, groupBase, originalTotal,
      brandingExpected, brandingActual, brandingIneligible
    };
  }

  function actualPersonalizedTotals(cart) {
    return (cart.items || []).reduce((totals, line) => {
      const props = line.properties || {};
      if (!clean(props['_ME Design Group'])) return totals;
      totals.original += cents(line.original_line_price);
      totals.final += cents(line.final_line_price);
      return totals;
    }, { original: 0, final: 0 });
  }

  function checkPricingReconciliation(cart) {
    const errors = [];
    const pricing = expectedPersonalizedSavings(cart);
    if (!pricing.totalQuantity) return errors;

    // Product discounts must reconcile in the cart because Shopify exposes them
    // on each line immediately. The earned group tier is an order discount and
    // is intentionally NOT compared to cart.final_line_price/cart.total_price;
    // Shopify applies that percentage at checkout.
    if (Math.abs(pricing.brandingActual - pricing.brandingExpected) > 1) {
      const money = (v) => `$${(Math.max(0, Number(v || 0)) / 100).toFixed(2)}`;
      errors.push(`Moments Etched branding credit does not match the selected inside neck label. Expected ${money(pricing.brandingExpected)} in product credit; Shopify is applying ${money(pricing.brandingActual)}.`);
    }
    if (pricing.brandingIneligible > 0) {
      errors.push('Moments Etched branding credit is applying to a garment that does not use the Moments Etched inside neck label.');
    }
    return errors;
  }

  async function sanitizeLegacyBrandingMarkers(cart) {
    const root = window.Shopify?.routes?.root || '/';
    let changed = false;
    for (const line of cart.items || []) {
      const props = line.properties || {};
      if (clean(props['_ME Line Type']) !== 'garment') continue;
      if (brandingCreditEligible(props)) continue;
      const hasRequested = Object.prototype.hasOwnProperty.call(props, '_ME Branding Credit Requested');
      const hasEligible = Object.prototype.hasOwnProperty.call(props, '_ME Branding Credit Eligible');
      if (!hasRequested && !hasEligible) continue;

      const nextProps = { ...props };
      delete nextProps['_ME Branding Credit Requested'];
      delete nextProps['_ME Branding Credit Eligible'];
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: Number(line.quantity || 0), properties: nextProps })
      });
      if (!response.ok) throw new Error('Unable to synchronize branding-credit eligibility.');
      changed = true;
    }
    return changed;
  }

  function checkProjectGroups(cart) {
    const errors = [];
    const groups = new Map();
    for (const line of cart.items || []) {
      const props = line.properties || {};
      const group = clean(props['_ME Design Group']);
      if (!group) continue;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(line);
    }

    for (const [group, lines] of groups.entries()) {
      const garments = lines.filter(line => clean(line.properties?.['_ME Line Type']) !== 'addon');
      const addons = lines.filter(line => clean(line.properties?.['_ME Line Type']) === 'addon');
      if (!garments.length) {
        errors.push(`Customization group ${group} contains pricing add-ons without a garment.`);
        continue;
      }
      const garmentQty = garments.reduce((sum, line) => sum + Number(line.quantity || 0), 0);

      // v15.4.7.33 — stale carts created by an older designer build may still
      // contain a 21+ custom-apparel project. The storefront quote threshold is
      // project-based, so fail closed at checkout for any single design group
      // containing 31 or more garment units. Do not silently delete the project.
      if (garmentQty >= 21) {
        errors.push('21+ custom apparel projects require a bulk quote and cannot be checked out directly. Return to the project and choose Request Bulk Quote.');
      }

      for (const garment of garments) {
        const props = garment.properties || {};
        if (!brandingCreditEligible(props) && brandingDiscountAmount(garment) > 0) {
          errors.push('Moments Etched branding credit is applying to a garment that does not use the Moments Etched inside neck label.');
        }
      }
      const selected = new Set();
      garments.forEach(line => splitPlacements(line.properties?.['Selected Print Locations']).forEach(p => selected.add(p)));
      for (const placement of selected) {
        const rule = paidRules()[placement];
        if (!rule || Number(rule.priceCents || 0) <= 0) continue;
        const paidQty = addons
          .filter(line => clean(line.properties?.Customization) === placement)
          .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
        if (paidQty !== garmentQty) errors.push(`${placement} pricing is attached to ${paidQty} of ${garmentQty} garments.`);
      }
      for (const addon of addons) {
        const placement = clean(addon.properties?.Customization);
        const rule = paidRules()[placement];
        if (!placement || !rule || Number(rule.priceCents || 0) <= 0) errors.push('A customization pricing line is not recognized.');
      }
    }
    return errors;
  }

  function checkFulfillmentGroups(cart) {
    const errors = [];
    const meta = renderedMeta();
    const groups = new Set();
    for (const item of meta) {
      const group = canonicalFulfillmentGroup(item?.fulfillmentGroup || item?.catalogFulfillmentGroup);
      if (group) groups.add(group);
    }
    // Fallback to line properties when rendered metadata is unavailable.
    if (!groups.size) {
      for (const line of cart.items || []) {
        const group = canonicalFulfillmentGroup(line?.properties?.['_ME Fulfillment Group']);
        if (group) groups.add(group);
      }
    }
    if (groups.size > 1) {
      const labels = {
        calca_dropship: 'DTF Supplies',
        local_dtf: 'Blank Apparel / Gang Sheets / Personalized Apparel / Ready to Wear',
        personalized_items: 'Personalized Items',
        wedding: 'Wedding Stationery',
        business: 'Business Services',
        standard: 'Standard'
      };
      errors.push(`Separate checkout required. This cart mixes ${[...groups].map(group => labels[group] || group).join(' + ')} fulfillment workflows. Please keep only one fulfillment group in this order.`);
    }
    return errors;
  }

  function checkTemplateRules(cart) {
    const errors = [];
    const meta = renderedMeta();
    const byKey = new Map(meta.map(item => [clean(item.key), item]));
    for (const line of cart.items || []) {
      const item = byKey.get(clean(line.key));
      if (!item) continue;
      const props = line.properties || {};
      const suffix = clean(item.templateSuffix);
      if (suffix === 'me-finished-apparel') {
        const required = ['_ME Product Type', '_Base Variant', 'Fit', 'Style', 'Color', 'Size'];
        const missing = required.filter(key => !clean(props[key]));
        if (missing.length) errors.push(`${clean(line.product_title) || 'Ready-to-wear design'} is missing required garment selections.`);
      }
      if ((suffix === 'me-custom' || suffix === 'me-premium-product') && !item.isBlank) {
        if (clean(props['_ME Line Type']) !== 'garment' || !clean(props['_ME Design Group']) || !clean(props['Selected Print Locations'])) {
          errors.push(`${clean(line.product_title) || 'Custom apparel'} must be added through the personalization studio.`);
        }
      }
    }
    return errors;
  }

  function setBlocked(errors) {
    const blocked = errors.length > 0;
    document.documentElement.classList.toggle('me-cart-integrity-blocked', blocked);
    qsa('#checkout, button[name="checkout"], a[href*="/checkout"], .shopify-payment-button__button, .additional-checkout-buttons button, .additional-checkout-buttons iframe').forEach(el => {
      if ('disabled' in el) el.disabled = blocked;
      el.setAttribute?.('aria-disabled', blocked ? 'true' : 'false');
    });
    let box = qs('[data-me-cart-integrity-alert]');
    if (!box) {
      box = document.createElement('div');
      box.dataset.meCartIntegrityAlert = '';
      box.className = 'me-cart-integrity-alert';
      box.setAttribute('role', 'alert');
      const target = qs('.me-checkout-first__ctas, .cart__ctas, #checkout')?.parentElement || qs('#cart-form') || document.body;
      target.prepend(box);
    }
    box.hidden = !blocked;
    if (blocked) {
      box.replaceChildren();
      const strong = document.createElement('strong');
      strong.textContent = 'Cart verification required';
      const span = document.createElement('span');
      span.textContent = errors.map(e => clean(e)).join(' ');
      const small = document.createElement('small');
      small.textContent = 'Checkout stays locked until the cart satisfies fulfillment separation, personalization, and pricing rules.';
      box.append(strong, span, small);
    }
  }

  async function verify() {
    try {
      let cart = await getCart();
      if (await sanitizeLegacyBrandingMarkers(cart)) cart = await getCart();
      const errors = [...checkFulfillmentGroups(cart), ...checkProjectGroups(cart), ...checkTemplateRules(cart), ...checkPricingReconciliation(cart)];
      setBlocked(Array.from(new Set(errors)));
      window.dispatchEvent(new CustomEvent('me:cart-integrity', { detail: { ok: !errors.length, errors, version: VERSION } }));
      return !errors.length;
    } catch (_) {
      setBlocked(['We could not verify the cart before checkout. Please refresh the page.']);
      return false;
    }
  }

  const state = { verified: false, ok: false };
  const originalVerify = verify;
  verify = async function verifiedCartIntegrity() {
    const ok = await originalVerify();
    state.verified = true;
    state.ok = ok;
    return ok;
  };

  function isCheckoutTrigger(target) {
    return target?.closest?.('#checkout, button[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons, .shopify-payment-button__button');
  }

  function serverLocked() {
    return Boolean(qs('[data-me-server-checkout-lock="true"]'));
  }

  function showBlocked() {
    qs('[data-me-cart-integrity-alert], [data-me-checkout-hard-lock]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (serverLocked()) setBlocked(['Shopify pricing does not match the configured personalized project. Checkout is locked until pricing is corrected.']);
    verify();
  });
  document.addEventListener('cart:update', verify);

  document.addEventListener('click', (event) => {
    const checkout = isCheckoutTrigger(event.target);
    if (!checkout) return;
    if (serverLocked() || document.documentElement.classList.contains('me-cart-integrity-blocked') || !state.verified || !state.ok) {
      // Prevent the browser / accelerated checkout from navigating before the
      // asynchronous verification has a chance to finish. v15.4.7.23 waited
      // until after await, which can be too late for a form navigation.
      event.preventDefault();
      event.stopImmediatePropagation();
      verify().then((ok) => {
        if (!ok || serverLocked()) {
          showBlocked();
          return;
        }
        // For the regular cart checkout button, replay the form submit once
        // the cart has been verified. Accelerated checkout buttons are not
        // programmatically replayed; the customer can select them again.
        const button = checkout.matches?.('#checkout, button[name="checkout"]') ? checkout : checkout.closest?.('#checkout, button[name="checkout"]');
        if (button?.form) button.form.requestSubmit(button);
      });
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    const isCheckout = submitter?.matches?.('#checkout, button[name="checkout"]') || clean(submitter?.name).toLowerCase() === 'checkout';
    if (!isCheckout) return;
    if (serverLocked() || document.documentElement.classList.contains('me-cart-integrity-blocked') || !state.verified || !state.ok) {
      event.preventDefault();
      event.stopImmediatePropagation();
      verify().then((ok) => { if (!ok || serverLocked()) showBlocked(); });
    }
  }, true);
  window.MECartIntegrity = { verify, version: VERSION };
})();
